import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { 
  parseIpcCn61Csv, 
  convertIpcCn61DataToCsv, 
  generateIpcCn61SampleCsv, 
  applyCsvDataToIpcCn61, 
  downloadIpcCn61Csv 
} from '../../../../utils/ipcCn61CsvUtils';

// Memoized TextField component defined outside to prevent recreation
const MemoizedTextField = React.memo(({ 
  value, 
  onChange, 
  onBlur,
  inputProps, 
  sx 
}: {
  value: string | number | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputProps: any;
  sx: any;
}) => (
  <TextField
    size="small"
    type="text"
    value={value || ''}
    onChange={onChange}
    onBlur={onBlur}
    sx={sx}
    inputProps={inputProps}
  />
), (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  return (
    prevProps.value === nextProps.value &&
    prevProps.onChange === nextProps.onChange &&
    prevProps.onBlur === nextProps.onBlur &&
    JSON.stringify(prevProps.inputProps) === JSON.stringify(nextProps.inputProps) &&
    JSON.stringify(prevProps.sx) === JSON.stringify(nextProps.sx)
  );
});

MemoizedTextField.displayName = 'MemoizedTextField';

interface Cn61InputData {
  weight?: {
    priority: string | null;
    nonPriority: string | null;
  };
  items?: {
    priority: string | null;
    nonPriority: string | null;
  };
}

interface Cn61Request {
  currency: string;
  inboundCn61Input: Cn61InputData | null;
  outboundCn61Input: Cn61InputData | null;
}

interface Cn61Data {
  flow: string;
  dto: {
    ppo: string;
    year: number;
    direction: string;
  };
  request: Cn61Request;
}

interface CN61TableProps {
  data: Cn61Data;
  onDataChange?: (updatedData: Cn61Data) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  currentUserPpo?: string;
}

function CN61Table({ data, onDataChange, onError, onSuccess, currentUserPpo }: CN61TableProps): React.ReactElement {
  const [localData, setLocalData] = React.useState<Cn61Data>(data);
  const [uploadErrors, setUploadErrors] = React.useState<string[]>([]);
  const [inboundFileInputRef] = React.useState<React.RefObject<HTMLInputElement>>(React.createRef());
  const [outboundFileInputRef] = React.useState<React.RefObject<HTMLInputElement>>(React.createRef());

  // Stable style objects to prevent recreation
  const textFieldSx = useMemo(() => ({
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: '32px',
      fontSize: '0.875rem'
    }
  }), []);

  const weightInputProps = useMemo(() => ({
    min: 0,
    step: 0.001,
    placeholder: "0.000",
    inputMode: 'decimal' as const,
    pattern: '^\\\d*(\\.\\d{0,3})?$',
  }), []);

  const itemsInputProps = useMemo(() => ({
    min: 0,
    step: 0.001,
    placeholder: "0.000",
    inputMode: 'decimal' as const,
    pattern: '^\\\d*(\\.\\d{0,3})?$',
  }), []);

  const formatDisplayValue = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return '';
    // Return as-is for string values (user is typing)
    return String(val);
  }, []);

  // Check if input has exactly 3 decimals (for visual validation)
  const hasValidFormat = useCallback((val: string | number | null) => {
    if (val === null || val === undefined || val === '') return false;
    const strVal = String(val);
    return /^\d+\.\d{3}$/.test(strVal);
  }, []);

  // Validate that all present fields are filled with exactly 3 decimals
  const threeDecimalsRegex = useMemo(() => /^\d+\.\d{3}$/, []);
  const isValueComplete = useCallback((val: unknown) => {
    if (val === null || val === undefined || val === '') return false;
    return threeDecimalsRegex.test(String(val));
  }, [threeDecimalsRegex]);

  const isDirectionComplete = useCallback((cn61Input: any | null) => {
    if (!cn61Input) return false;
    let complete = true;
    Object.values(cn61Input).forEach((entry: any) => {
      if (!entry || typeof entry !== 'object') return;
      if (entry.weight) {
        if (!isValueComplete(entry.weight.priority) || !isValueComplete(entry.weight.nonPriority)) complete = false;
      }
      if (entry.items) {
        if (!isValueComplete(entry.items.priority) || !isValueComplete(entry.items.nonPriority)) complete = false;
      }
    });
    return complete;
  }, [isValueComplete]);

  // Debounced parent notification to avoid excessive re-renders
  const debouncedNotifyParent = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (newData: Cn61Data) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (onDataChange) {
          onDataChange(newData);
        }
      }, 300); // 300ms debounce
    };
  }, [onDataChange]);

  // Initialize local data and only update when prop data reference changes
  React.useEffect(() => {
    setLocalData(data);
  }, [data]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="cn61-table-empty">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="cn61-table-empty-text">
          No data available
        </Typography>
      </Box>
    );
  }

  const { flow, dto, request } = localData;
  const hasInboundData = request.inboundCn61Input !== null;
  const hasOutboundData = request.outboundCn61Input !== null;

  if (!hasInboundData && !hasOutboundData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }} data-testid="cn61-table-no-input">
        <Typography sx={{ color: '#666', fontStyle: 'italic' }} data-testid="cn61-table-no-input-text">
          No data available
        </Typography>
      </Box>
    );
  }

  // Get dynamic rows from BE data instead of hardcoding - stable function
  const getDynamicRows = React.useCallback((cn61Input: Cn61InputData) => {
    const rows: Array<{
      key: string;
      label: string;
      hasWeight: boolean;
      hasItems: boolean;
      data: any;
    }> = [];

    // Iterate through the actual data from BE
    Object.entries(cn61Input).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        const hasWeight = 'weight' in value && value.weight !== null;
        const hasItems = 'items' in value && value.items !== null;
        
        // Only add rows that have actual data
        if (hasWeight || hasItems) {
          rows.push({
            key,
            label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
            hasWeight,
            hasItems,
            data: value,
          });
        }
      }
    });

    return rows;
  }, []);

  // Memoize the dynamic rows for each direction to prevent recalculation
  const inboundRows = useMemo(() => {
    return hasInboundData ? getDynamicRows(request.inboundCn61Input!) : [];
  }, [hasInboundData, request.inboundCn61Input, getDynamicRows]);

  const outboundRows = useMemo(() => {
    return hasOutboundData ? getDynamicRows(request.outboundCn61Input!) : [];
  }, [hasOutboundData, request.outboundCn61Input, getDynamicRows]);

  const handleInputChange = useCallback((
    direction: 'inbound' | 'outbound',
    rowKey: string, 
    field: 'weight' | 'items', 
    type: 'priority' | 'nonPriority', 
    value: string
  ) => {
    setLocalData((prev) => {
      const newData = { ...prev };
      const inputKey = direction === 'inbound' ? 'inboundCn61Input' : 'outboundCn61Input';
      
      if (newData.request[inputKey]) {
        const updatedInput = { ...newData.request[inputKey] };
        
        // Initialize the rowKey object if it doesn't exist
        if (!updatedInput[rowKey as keyof typeof updatedInput]) {
          (updatedInput as any)[rowKey] = {};
        }
        
        // Initialize the field object if it doesn't exist
        if (!(updatedInput as any)[rowKey][field]) {
          (updatedInput as any)[rowKey][field] = {};
        }
        
        // Update the specific value - store as string to preserve user input
        (updatedInput as any)[rowKey][field][type] = value === '' ? null : value;
        
        newData.request[inputKey] = updatedInput as any;
      }
      
      // Debounced notification to parent during typing
      debouncedNotifyParent(newData);
      
      return newData;
    });
  }, [debouncedNotifyParent]);

  const handleInputBlur = useCallback((
    direction: 'inbound' | 'outbound',
    rowKey: string, 
    field: 'weight' | 'items', 
    type: 'priority' | 'nonPriority', 
    value: string
  ) => {
    // Enforce up to 3 decimals; coerce to 3-decimal string if valid
    const sanitized = value.trim();
    const isValid = /^\d*(\.\d{0,3})?$/.test(sanitized);
    if (isValid && sanitized !== '') {
      const numericValue = parseFloat(sanitized);
      const formattedValue = isNaN(numericValue) ? '' : numericValue.toFixed(3);
      
      // Update local data with formatted value
      setLocalData((prev) => {
        const newData = { ...prev };
        const inputKey = direction === 'inbound' ? 'inboundCn61Input' : 'outboundCn61Input';
        
        if (newData.request[inputKey]) {
          const updatedInput = { ...newData.request[inputKey] };
          
          if (!updatedInput[rowKey as keyof typeof updatedInput]) {
            (updatedInput as any)[rowKey] = {};
          }
          
          if (!(updatedInput as any)[rowKey][field]) {
            (updatedInput as any)[rowKey][field] = {};
          }
          
          (updatedInput as any)[rowKey][field][type] = formattedValue;
          newData.request[inputKey] = updatedInput as any;
        }
        
        // Immediately notify parent on blur with final formatted value
        if (onDataChange) {
          onDataChange(newData);
        }
        
        return newData;
      });
    }
  }, [onDataChange]);

  // CSV handling functions
  const handleCsvDownload = useCallback((direction: 'inbound' | 'outbound') => {
    try {
      // Guard: block download if any present fields are incomplete
      const targetInput = direction === 'inbound' ? localData.request.inboundCn61Input : localData.request.outboundCn61Input;
      if (!isDirectionComplete(targetInput)) {
        onError?.('Please fill all TD rates with exactly 3 decimals before downloading');
        return;
      }
      // Create a filtered data structure for the specific direction
      const filteredData = {
        ...localData,
        request: {
          ...localData.request,
          inboundCn61Input: direction === 'inbound' ? localData.request.inboundCn61Input : null,
          outboundCn61Input: direction === 'outbound' ? localData.request.outboundCn61Input : null
        }
      };
      
      const csvContent = convertIpcCn61DataToCsv(filteredData, currentUserPpo);
      const filename = direction === 'inbound' 
        ? `td_rates_${currentUserPpo}_${localData.dto.ppo}_${localData.dto.year}.csv`
        : `td_rates_${localData.dto.ppo}_${currentUserPpo}_${localData.dto.year}.csv`;
      downloadIpcCn61Csv(csvContent, filename);
      onSuccess?.(`CSV file for ${direction} downloaded successfully`);
    } catch (error) {
      onError?.(`Error generating CSV file: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [localData, currentUserPpo, onSuccess, onError, isDirectionComplete]);

  const handleCsvUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, direction: 'inbound' | 'outbound') => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Clear previous errors
    setUploadErrors([]);

    try {
      const text = await file.text();
      
      // Parse CSV with year validation
      const csvData = parseIpcCn61Csv(text, localData, currentUserPpo, localData.dto.year);
      
      // Filter CSV data by direction
      const filteredCsvData = csvData.filter(row => row.direction === direction);
      
      if (filteredCsvData.length === 0) {
        const errorMsg = `CSV file contains no valid data rows for ${direction} direction`;
        setUploadErrors([errorMsg]);
        onError?.(errorMsg);
        return;
      }

      const updatedData = applyCsvDataToIpcCn61(filteredCsvData, localData);
      setLocalData(updatedData);
      onDataChange?.(updatedData);
      // Clear any previous errors on successful upload
      setUploadErrors([]);
      onSuccess?.(`CSV file for ${direction} uploaded successfully`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Split multiple errors if they were joined with |||
      const errorList = errorMessage.includes('|||') 
        ? errorMessage.split('|||') 
        : [`Error parsing CSV file: ${errorMessage}`];
      
      setUploadErrors(errorList);
      onError?.(errorList.join('\n'));
    } finally {
      // Reset file input
      event.target.value = '';
    }
  }, [localData, currentUserPpo, onDataChange, onError, onSuccess]);

  const handleSampleCsvDownload = useCallback(() => {
    try {
      const sampleCsv = generateIpcCn61SampleCsv(currentUserPpo, localData.dto.ppo, localData.dto.year);
      downloadIpcCn61Csv(sampleCsv, 'ipc_cn61_sample.csv');
      onSuccess?.('Sample CSV downloaded successfully');
    } catch (error) {
      onError?.(`Error generating sample CSV: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, [currentUserPpo, localData.dto.ppo, localData.dto.year, onSuccess, onError]);

  // Background colors based on direction - memoized to prevent recreation
  const getBackgroundColor = useCallback((isInbound: boolean, isMainRow: boolean) => {
    if (isInbound) {
      return isMainRow ? '#e8f5e8' : '#f0f8f0'; // Green shades for inbound
    } else {
      return isMainRow ? '#ffe8e8' : '#fff0f0'; // Red shades for outbound
    }
  }, []);

  // Helper function to render a single table for a specific direction - memoized
  const renderDirectionTable = useCallback((cn61Input: Cn61InputData, isInbound: boolean, direction: string, dynamicRows: any[]) => {
    const directionKey = isInbound ? 'inbound' : 'outbound';
    const fileInputRef = isInbound ? inboundFileInputRef : outboundFileInputRef;

    return (
      <Box sx={{ mb: 3 }}>
        <TableContainer component={Paper} sx={{ mb: 2 }} data-testid={`cn61-table-${direction.toLowerCase()}`}>
          <Table size="small" sx={{ border: '1px solid #e0e0e0' }}>
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    backgroundColor: '#f5f5f5', 
                    border: '1px solid #e0e0e0',
                    textAlign: 'center'
                  }}
                  colSpan={2}
                >
                  {direction}
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    backgroundColor: '#f5f5f5', 
                    width: '125px',
                    border: '1px solid #e0e0e0',
                    textAlign: 'center'
                  }}
                >
                  Priority
                </TableCell>
                <TableCell 
                  sx={{ 
                    fontWeight: 700, 
                    backgroundColor: '#f5f5f5', 
                    width: '125px',
                    border: '1px solid #e0e0e0',
                    textAlign: 'center'
                  }}
                >
                  Non-Priority
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dynamicRows.map((rowDef) => (
                <React.Fragment key={rowDef.key}>
                  {/* Weight row */}
                  {rowDef.hasWeight && (
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 600, 
                          backgroundColor: getBackgroundColor(isInbound, true),
                          border: '1px solid #e0e0e0',
                          borderBottom: rowDef.hasItems ? '1px solid #e0e0e0' : '1px solid #e0e0e0'
                        }}
                        rowSpan={rowDef.hasItems ? 2 : 1}
                      >
                        {rowDef.label}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          backgroundColor: getBackgroundColor(isInbound, false),
                          border: '1px solid #e0e0e0',
                          borderBottom: rowDef.hasItems ? '1px solid #e0e0e0' : '1px solid #e0e0e0',
                          fontSize: '0.875rem',
                          textAlign: 'left',
                          fontWeight: 500,
                          width: '120px'
                        }}
                      >
                        Weight
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: '1px solid #e0e0e0',
                          borderBottom: rowDef.hasItems ? '1px solid #e0e0e0' : '1px solid #e0e0e0'
                        }}
                      >
                        <TextField
                          size="small"
                          type="text"
                          value={formatDisplayValue(rowDef.data.weight?.priority)}
                          onChange={(e) => handleInputChange(directionKey, rowDef.key, 'weight', 'priority', e.target.value)}
                          onBlur={(e) => handleInputBlur(directionKey, rowDef.key, 'weight', 'priority', e.target.value)}
                          sx={{
                            ...textFieldSx,
                            '& .MuiOutlinedInput-root': {
                              ...textFieldSx['& .MuiOutlinedInput-root'],
                              '& fieldset': {
                                borderColor: rowDef.data.weight?.priority && !hasValidFormat(rowDef.data.weight.priority) ? '#d32f2f' : undefined,
                              },
                            },
                          }}
                          inputProps={weightInputProps}
                          error={rowDef.data.weight?.priority ? !hasValidFormat(rowDef.data.weight.priority) : false}
                        />
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          border: '1px solid #e0e0e0',
                          borderBottom: rowDef.hasItems ? '1px solid #e0e0e0' : '1px solid #e0e0e0'
                        }}
                      >
                        <TextField
                          size="small"
                          type="text"
                          value={formatDisplayValue(rowDef.data.weight?.nonPriority)}
                          onChange={(e) => handleInputChange(directionKey, rowDef.key, 'weight', 'nonPriority', e.target.value)}
                          onBlur={(e) => handleInputBlur(directionKey, rowDef.key, 'weight', 'nonPriority', e.target.value)}
                          sx={{
                            ...textFieldSx,
                            '& .MuiOutlinedInput-root': {
                              ...textFieldSx['& .MuiOutlinedInput-root'],
                              '& fieldset': {
                                borderColor: rowDef.data.weight?.nonPriority && !hasValidFormat(rowDef.data.weight.nonPriority) ? '#d32f2f' : undefined,
                              },
                            },
                          }}
                          inputProps={weightInputProps}
                          error={rowDef.data.weight?.nonPriority ? !hasValidFormat(rowDef.data.weight.nonPriority) : false}
                        />
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Items row */}
                  {rowDef.hasItems && (
                    <TableRow>
                      {!rowDef.hasWeight && (
                        <TableCell 
                          sx={{ 
                            fontWeight: 600, 
                            backgroundColor: getBackgroundColor(isInbound, true),
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          {rowDef.label}
                        </TableCell>
                      )}
                      <TableCell 
                        sx={{ 
                          backgroundColor: getBackgroundColor(isInbound, false),
                          border: '1px solid #e0e0e0',
                          fontSize: '0.875rem',
                          textAlign: 'left',
                          fontWeight: 500,
                          width: '120px'
                        }}
                      >
                        Items
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        <TextField
                          size="small"
                          type="text"
                          value={formatDisplayValue(rowDef.data.items?.priority)}
                          onChange={(e) => handleInputChange(directionKey, rowDef.key, 'items', 'priority', e.target.value)}
                          onBlur={(e) => handleInputBlur(directionKey, rowDef.key, 'items', 'priority', e.target.value)}
                          sx={{
                            ...textFieldSx,
                            '& .MuiOutlinedInput-root': {
                              ...textFieldSx['& .MuiOutlinedInput-root'],
                              '& fieldset': {
                                borderColor: rowDef.data.items?.priority && !hasValidFormat(rowDef.data.items.priority) ? '#d32f2f' : undefined,
                              },
                            },
                          }}
                          inputProps={itemsInputProps}
                          error={rowDef.data.items?.priority ? !hasValidFormat(rowDef.data.items.priority) : false}
                        />
                      </TableCell>
                      <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                        <TextField
                          size="small"
                          type="text"
                          value={formatDisplayValue(rowDef.data.items?.nonPriority)}
                          onChange={(e) => handleInputChange(directionKey, rowDef.key, 'items', 'nonPriority', e.target.value)}
                          onBlur={(e) => handleInputBlur(directionKey, rowDef.key, 'items', 'nonPriority', e.target.value)}
                          sx={{
                            ...textFieldSx,
                            '& .MuiOutlinedInput-root': {
                              ...textFieldSx['& .MuiOutlinedInput-root'],
                              '& fieldset': {
                                borderColor: rowDef.data.items?.nonPriority && !hasValidFormat(rowDef.data.items.nonPriority) ? '#d32f2f' : undefined,
                              },
                            },
                          }}
                          inputProps={itemsInputProps}
                          error={rowDef.data.items?.nonPriority ? !hasValidFormat(rowDef.data.items.nonPriority) : false}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Action buttons below the table */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2, mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => handleCsvDownload(directionKey)}
            sx={{
              borderColor: '#00ACE7',
              color: '#00ACE7',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              padding: '6px 16px',
              '&:hover': {
                borderColor: '#0095C7',
                backgroundColor: '#E3F7FE',
              },
            }}
            data-testid={`ipc-cn61-download-${directionKey}-csv-btn`}
          >
            Download TD Rates
          </Button>
          <Button
            variant="outlined"
            onClick={() => fileInputRef.current?.click()}
            sx={{
              borderColor: '#00ACE7',
              color: '#00ACE7',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              padding: '6px 16px',
              '&:hover': {
                borderColor: '#0095C7',
                backgroundColor: '#E3F7FE',
              },
            }}
            data-testid={`ipc-cn61-upload-${directionKey}-csv-btn`}
          >
            Upload TD Rates
          </Button>
        </Box>

        {/* Hidden file input for CSV upload */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={(e) => handleCsvUpload(e, directionKey)}
          style={{ display: 'none' }}
          data-testid={`ipc-cn61-csv-file-input-${directionKey}`}
        />
      </Box>
    );
  }, [getBackgroundColor, handleInputChange, handleInputBlur, handleCsvDownload, handleCsvUpload, inboundFileInputRef, outboundFileInputRef]);

  return (
    <Box sx={{ width: '100%' }} data-testid="cn61-table-container">
      {/* Report Header */}
      <Box sx={{ mb: 3 }} data-testid="cn61-table-header">
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#063142' }} data-testid="cn61-table-title">
            CN61 {flow.toUpperCase()} Report - {dto.ppo}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#666' }} data-testid="cn61-table-year">
          Year: {dto.year} | Currency: {request.currency}
        </Typography>
      </Box>

      {/* Upload Errors Display */}
      {uploadErrors.length > 0 && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="ipc-cn61-upload-errors">
          <AlertTitle>Upload Errors</AlertTitle>
          <List dense sx={{ pt: 0 }}>
            {uploadErrors.map((error, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemText primary={error} />
              </ListItem>
            ))}
          </List>
        </Alert>
      )}

      {/* CN61 Data Tables */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: hasInboundData && hasOutboundData ? 'row' : 'column', 
        gap: 4,
        flexWrap: 'wrap'
      }}>
        {/* Inbound Table */}
        {hasInboundData && (
          <Box 
            data-testid="cn61-inbound-section"
            sx={{ 
              flex: hasInboundData && hasOutboundData ? '1 1 45%' : '1',
              minWidth: '500px',
              maxWidth: '500px'
            }}
          >
            {renderDirectionTable(request.inboundCn61Input!, true, 'Inbound', inboundRows)}
          </Box>
        )}

        {/* Outbound Table */}
        {hasOutboundData && (
          <Box 
            data-testid="cn61-outbound-section"
            sx={{ 
              flex: hasInboundData && hasOutboundData ? '1 1 45%' : '1',
              minWidth: '500px',
              maxWidth: '500px'
            }}
          >
            {renderDirectionTable(request.outboundCn61Input!, false, 'Outbound', outboundRows)}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default CN61Table;
